import * as React from 'react';
import { showErrorMessage } from '@/shared/components/toast-system';
import { notifyAiLimit } from '@/shared/utils/apiError';
import { checkSpeechToTextEnabled, transcribeAudio } from '@utils/speechApiUtils';

type SpeechMode = 'server' | 'browser' | null;

interface UseSpeechToTextResult {
  isListening: boolean;
  isTranscribing: boolean;
  isSupported: boolean;
  toggleListening: () => void;
}

const isServerSpeechConfigured = (): boolean => {
  const envVal: unknown = import.meta.env?.REACT_APP_SERVER_SPEECH_API;
  return envVal === 'true' || envVal === true;
};

const toErrorLike = (err: unknown): { name?: string; message?: string } =>
  typeof err === 'object' && err !== null ? err : {};

const getBrowserSpeechRecognition = (): typeof SpeechRecognition | null => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ?? null;
};

export const isFirefoxBrowser = (): boolean => /Firefox/i.test(navigator.userAgent);

const useSpeechMode = (): SpeechMode => {
  const [mode, setMode] = React.useState<SpeechMode>(null);

  React.useEffect(() => {
    let cancelled = false;

    const determine = async () => {
      if (isServerSpeechConfigured() && navigator.mediaDevices) {
        try {
          const { enabled } = await checkSpeechToTextEnabled();
          if (!cancelled && enabled) {
            setMode('server');
            return;
          }
        } catch {
          // Server unreachable or errored — fall through to browser check.
        }
      }

      if (!cancelled) {
        const SR = getBrowserSpeechRecognition();
        setMode(SR ? 'browser' : null);
      }
    };

    determine();
    return () => {
      cancelled = true;
    };
  }, []);

  return mode;
};

/**
 * Dictation for a textarea: records through the server transcription API when the
 * build and backend both have it enabled, otherwise falls back to the browser's
 * native SpeechRecognition. Inserts the transcript at the caret via the native
 * value setter + a dispatched `input` event, so it works through a React-controlled
 * `value`/`onChange` without the hook needing to know the caller's state shape.
 */
export const useSpeechToText = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
): UseSpeechToTextResult => {
  const [isListening, setIsListening] = React.useState(false);
  const [isTranscribing, setIsTranscribing] = React.useState(false);
  const recognitionRef = React.useRef<SpeechRecognition | null>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const audioChunksRef = React.useRef<Blob[]>([]);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const audioCtxRef = React.useRef<AudioContext | null>(null);
  const sourceNodeRef = React.useRef<MediaStreamAudioSourceNode | null>(null);

  const speechMode = useSpeechMode();
  const isSupported = speechMode !== null;

  const setTextareaValue = React.useCallback(
    (newValue: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const nativeSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
      if (nativeSetter) {
        nativeSetter.call(textarea, newValue);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    [textareaRef],
  );

  const insertTranscript = React.useCallback(
    (transcript: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart ?? textarea.value.length;
      const end = textarea.selectionEnd ?? textarea.value.length;
      const currentValue = textarea.value;
      const prefix = currentValue.slice(0, start);
      const suffix = currentValue.slice(end);
      const separator = prefix.length > 0 && !prefix.endsWith(' ') ? ' ' : '';
      const newValue = prefix + separator + transcript + suffix;

      setTextareaValue(newValue);
    },
    [setTextareaValue, textareaRef],
  );

  const stopServerRecording = React.useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startServerRecording = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.fftSize);
      const SILENCE_THRESHOLD = 10;
      const SILENCE_DURATION_MS = 4000;
      const MIN_RECORDING_MS = 2000;
      const recordingStartTime = Date.now();
      let silenceStart: number | null = null;

      const checkSilence = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length) * 100;

        if (rms < SILENCE_THRESHOLD) {
          if (silenceStart === null) silenceStart = Date.now();
          const recordingElapsed = Date.now() - recordingStartTime;
          if (recordingElapsed > MIN_RECORDING_MS && Date.now() - silenceStart > SILENCE_DURATION_MS) {
            stopServerRecording();
            return;
          }
        } else {
          silenceStart = null;
        }

        animationFrameRef.current = requestAnimationFrame(checkSilence);
      };

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstart = () => {
        setIsListening(true);
        animationFrameRef.current = requestAnimationFrame(checkSilence);
      };

      recorder.onstop = async () => {
        setIsListening(false);
        setIsTranscribing(true);

        analyserRef.current = null;
        sourceNodeRef.current?.disconnect();
        sourceNodeRef.current = null;
        audioCtxRef.current?.close();
        audioCtxRef.current = null;

        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

          if (audioBlob.size < 500) {
            setIsTranscribing(false);
            return;
          }

          const data = await transcribeAudio(audioBlob, mimeType);
          if (data.text) insertTranscript(data.text);
        } catch (err) {
          if (!notifyAiLimit(err)) showErrorMessage(toErrorLike(err).message || 'Transcription failed. Please try again.');
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start(250);
    } catch (err) {
      const errorLike = toErrorLike(err);
      if (errorLike.name === 'NotAllowedError') {
        showErrorMessage('Microphone access was denied. Please allow microphone access in your browser settings.');
      } else {
        showErrorMessage(errorLike.message || 'Could not access microphone. Please check permissions.');
      }
      setIsListening(false);
    }
  }, [insertTranscript, stopServerRecording]);

  const toggleBrowserListening = React.useCallback(() => {
    const SR = getBrowserSpeechRecognition();
    if (!SR) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = navigator.language || 'en-US';
    recognitionRef.current = recognition;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(' ');
      insertTranscript(transcript);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.start();
  }, [isListening, insertTranscript]);

  const toggleListening = React.useCallback(() => {
    if (speechMode === 'server') {
      if (isListening) stopServerRecording();
      else void startServerRecording();
    } else {
      toggleBrowserListening();
    }
  }, [speechMode, isListening, stopServerRecording, startServerRecording, toggleBrowserListening]);

  React.useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      stopServerRecording();
    };
  }, [stopServerRecording]);

  return { isListening, isTranscribing, isSupported, toggleListening };
};
